import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "amazing-care-theme";

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggle: () => void;
}

export function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? systemTheme() : preference;
}

/** Base UI and Tailwind both key off the `dark` class on the root element. */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
  return resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      preference: "system",
      setPreference: (preference) => {
        applyTheme(preference);
        set({ preference });
      },
      toggle: () => {
        const next: ThemePreference = resolveTheme(get().preference) === "dark" ? "light" : "dark";
        applyTheme(next);
        set({ preference: next });
      },
    }),
    { name: THEME_STORAGE_KEY }
  )
);

/** Resolved theme for consumers that need the concrete value, e.g. the toaster. */
export function useResolvedTheme(): ResolvedTheme {
  return resolveTheme(useThemeStore((state) => state.preference));
}
